import { gql, useQuery } from '@apollo/client';
import { EditorPlugin } from '@draft-js-plugins/editor';
import { MentionData } from '@draft-js-plugins/mention/lib/';
import { MentionSuggestionsPubProps } from '@draft-js-plugins/mention/lib/MentionSuggestions/MentionSuggestions';
import '@draft-js-plugins/mention/lib/plugin.css';
import * as React from 'react';

import { SearchHelpers } from '@/client/legacy-shared-modules/search/helpers';
import { IMemberSearch } from '@/client/legacy-shared-modules/search/search-types';
import { usePermissions } from '@/hooks/usePermissions';
import { useCommunityUsers } from '@/imports/ui/hooks/useCommunityUsers';

import { mentionPlugin } from '../../components/MentionSuggestionsWrapper/MentionSuggestionsWrapper';
import { Entry } from './components/Entry';
import { formatDisplayName } from '@/utilities';

export const useMentions = (): {
  loading: boolean;
  currentUserHasPermission: boolean;
  mentionPlugin: EditorPlugin & {
    MentionSuggestions: React.ComponentType<MentionSuggestionsPubProps>;
  };
  mentionSuggestionsProps: MentionSuggestionsPubProps;
} => {
  const [suggestions, setSuggestions] = React.useState<MentionData[]>([]);
  const [open, onOpenChange] = React.useState(false);
  const { canAccessCommunityAndAuthenticated, isLoading } = usePermissions();

  const {
    loading,
    indexName,
    searchClient,
    viewableAttributesField,
    searchableAttributesField,
  } = useCommunityUsers(!canAccessCommunityAndAuthenticated);

  if (
    loading ||
    isLoading ||
    (canAccessCommunityAndAuthenticated &&
      (!viewableAttributesField || !searchableAttributesField))
  ) {
    return {
      loading: true,
      currentUserHasPermission: false,
      mentionPlugin,
      mentionSuggestionsProps: {
        open,
        onOpenChange,
        suggestions,
        onSearchChange: function () {},
      },
    };
  }

  if (!isLoading && !canAccessCommunityAndAuthenticated) {
    return {
      loading: false,
      currentUserHasPermission: false,
      mentionPlugin,
      mentionSuggestionsProps: {
        open,
        onOpenChange,
        suggestions,
        onSearchChange: function () {},
      },
    };
  }

  if (!indexName || !searchClient) {
    throw new Error('index name or search client was not defined');
  }

  const index = searchClient.initIndex(indexName);

  const onSearchChange = (event: { value: string }): Promise<void> => {
    return index
      .search<IMemberSearch>(event.value, { hitsPerPage: 5 })
      .then(({ hits }) => {
        setSuggestions(
          hits.map((hit) => {
            const typedviewableAttributesField = viewableAttributesField as
              | 'adminMemberDirectoryViewable'
              | 'membersWithMemberStatusMemberDirectoryViewable'
              | 'loggedInMemberDirectoryViewable';

            const memberAttributes = hit[typedviewableAttributesField];

            return {
              id: hit.userId,
              name: memberAttributes ? formatDisplayName(memberAttributes) : hit.displayName || '',
              userId: hit.userId,
              link: SearchHelpers.getMemberUrl(hit),
              avatar: hit.profilePictureUrl ?? undefined,
              jobTitle: memberAttributes?.jobTitle,
              company: memberAttributes?.company,
              nameTitle: memberAttributes?.nameTitle,
            };
          })
        );
      });
  };

  return {
    loading,
    currentUserHasPermission: canAccessCommunityAndAuthenticated,
    mentionPlugin,
    mentionSuggestionsProps: {
      open,
      onOpenChange,
      suggestions,
      onSearchChange,
      entryComponent: Entry,
    },
  };
};
